import { UserDto } from '@act/shared/data-transfer-objects';
import { FormBuilder } from '@act/storybook';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  CircularProgress,
  Box,
} from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { GenericDialogProps } from 'utilities/dialog-context/dialog-context-helpers';
import {
  DialogContentContainer,
  DialogTitleBarComponent,
} from './components/SharedDialogHookComponents';
import {
  formatRaceEthnicitySummaryForDatabase,
  getLanguage,
  getRaceEthnicity,
} from './helpers/personal-information-formatters';
import { Button } from '@act/storybook';
import useUnsavedChangesDialog from './hooks/use-unsaved-changes-dialog';
import { useApiCall } from 'redux/utils/api-hook';
import { userRequests } from 'redux/requests/users.request';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import ETHNICITY_OPTIONS from 'redux/constants/ethnicityOptions';
import LANGUAGE_OPTIONS from 'redux/constants/languageOptions';
import PRONOUN_OPTIONS from 'redux/constants/pronounOptions';
import RACE_OPTIONS from 'redux/constants/raceOptions';
import { useToast } from '../app/ReusableToast';
import { useAppDispatch } from 'redux/store-hooks';
import { TrackingProvider } from 'react-components/tracking';
import { getMember } from 'redux/slices/memberSlice';

export interface PersonalInformationUniqueProps {
  member: UserDto;
}

export type PersonalInformationDialogProps = GenericDialogProps<boolean> &
  PersonalInformationUniqueProps;

export type PersonalInformationDialogCallbackInput = {};

export interface PersonalInformationForm {
  userId: string;
  preferredName: string;
  namePronunciation: string;
  pronouns: string;
  race: string;
  ethnicity: string;
  language: string;
}

export const PersonalInformationDialog = ({
  member,
  open,
  setOpen,
  callback,
}: PersonalInformationDialogProps) => {
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const [
    updateMemberInfoResult,
    isLoading,
    updateMemberInfo,
    updateMemberInfoError,
  ] = useApiCall(userRequests.patchMemberInfo);

  const { control, handleSubmit, reset, formState } =
    useForm<PersonalInformationForm>({
      defaultValues: {
        userId: member?.id || '',
        preferredName: '',
        namePronunciation: '',
        pronouns: '',
        race: '',
        ethnicity: '',
        language: '',
      },
    });

  const { isDirty } = formState;

  useEffect(() => {
    if (member) {
      const { race, ethnicity } = getRaceEthnicity(
        member?.raceEthnicitySummary
      );
      reset({
        userId: member?.id,
        preferredName: member?.preferredName || '',
        namePronunciation: member?.namePronunciation || '',
        pronouns: member?.pronouns || '',
        race,
        ethnicity,
        language: getLanguage(member?.reportedLanguage),
      });
    }
  }, [member]);

  useEffect(() => {
    if (updateMemberInfoResult && !isLoading) {
      dispatch(getMember({ params: { id: member.id } }));
      setOpen(false);
      callback(true);
      toast("Member's personal information has been updated successfully");
    }
  }, [updateMemberInfoResult, updateMemberInfoError, isLoading]);

  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
        callback(true);
      });
    } else {
      setOpen(false);
      callback(true);
    }
  };

  const formConfig: FormConfig<PersonalInformationForm> = [
    {
      type: 'input',
      name: 'preferredName',
      label: 'Preferred Name',
      input: 'text',
      props: {
        trackingName: 'preferred-name-input',
      },
    },
    {
      type: 'input',
      name: 'namePronunciation',
      label: 'Name Pronunciation',
      input: 'text',
      props: {
        trackingName: 'name-pronunciation-input',
      },
    },
    {
      type: 'label',
      label: "Select the member's preferred pronouns",
    },
    {
      type: 'input',
      name: 'pronouns',
      label: 'Preferred Pronouns',
      input: 'select',
      props: {
        options: PRONOUN_OPTIONS.map(pronouns => ({
          key: pronouns,
          label: pronouns,
          value: pronouns,
        })),
        fullWidth: true,
        trackingName: 'pronouns-select',
      },
    },
    {
      type: 'label',
      label: "Select the member's race and ethnicity",
    },
    {
      type: 'input',
      name: 'race',
      label: 'Race',
      input: 'select',
      props: {
        options: RACE_OPTIONS.map(race => ({
          key: race,
          label: race,
          value: race,
        })),
        fullWidth: true,
        trackingName: 'race-select',
      },
    },
    {
      type: 'input',
      name: 'ethnicity',
      label: 'Ethnicity',
      input: 'radioGroup',
      props: {
        options: ETHNICITY_OPTIONS.map(ethnicity => ({
          key: ethnicity,
          label: ethnicity,
          value: ethnicity,
        })),
        trackingName: 'ethnicity-radio-group',
      },
    },
    {
      type: 'label',
      label: "Select the member's preferred language",
    },
    {
      type: 'input',
      name: 'language',
      label: 'Preferred Language',
      input: 'select',
      props: {
        options: LANGUAGE_OPTIONS.map(language => ({
          key: language,
          label: language,
          value: language,
        })),
        fullWidth: true,
        trackingName: 'language-select',
      },
    },
  ];

  async function onSubmit(data: PersonalInformationForm) {
    const {
      ethnicity,
      language,
      namePronunciation,
      preferredName,
      pronouns,
      race,
      userId,
    } = data;
    const summary = formatRaceEthnicitySummaryForDatabase(race, ethnicity);
    await updateMemberInfo({
      body: {
        namePronunciation,
        preferredName,
        pronouns,
        reportedLanguage: language,
        raceEthnicitySummary: summary,
      },
      params: { memberId: userId },
    });
  }

  return (
    <TrackingProvider contextName="personal-information-dialog">
      <Box sx={{ width: '600px' }}>
        <Dialog open={open} onClose={handleClose} fullWidth>
          <DialogContentContainer>
            <DialogTitleBarComponent
              title="Edit personal information"
              handleClose={handleClose}
            />
            <DialogContent>
              <FormBuilder
                config={formConfig}
                control={control}
                formState={formState}
              />
            </DialogContent>
            <Divider />
            <DialogActions sx={{ padding: '8px 24px' }}>
              <Button
                onClick={handleClose}
                disabled={isLoading}
                trackingName="cancel-button"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                debounced
                onClick={handleSubmit(onSubmit)}
                sx={{ padding: '10px 24px' }}
                disabled={isLoading}
                startIcon={
                  isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
                trackingName="save-personal-info-button"
              >
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
            </DialogActions>
          </DialogContentContainer>
        </Dialog>
      </Box>
    </TrackingProvider>
  );
};
